import { localStorageProvider } from "../../../AppController";
import { parseJwt } from "../../../util/jwt";

// ToDo: create independent type once service is update
export type IAuthServiceImplFactory = ReturnType<typeof AuthServiceImplFactory>;

export const AuthServiceImplFactory = ({
  authProvider,
  circadiaEngineProvider,
  electronEngineProvider,
}: {
  authProvider: IAuthProvider;
  circadiaEngineProvider: ICircadiaEngineProvider;
  electronEngineProvider: IElectronEngineProvider;
}) => {
  const isCircadiaEngine = circadiaEngineProvider.isWindowCircadia();
  const isElectronEngine = electronEngineProvider.isWindowElectron();

  const getCurrentSessionPayload = async () => {
    if (isElectronEngine) {
      const session = await electronEngineProvider.currentSession();
      return session.sessionPayload;
    } else {
      return (await authProvider.currentSession()).getIdToken().payload;
    }
  };

  const getCurrentUserInfo = () => {
    if (localStorageProvider.get("zpax") === "true") {
      return authProvider.currentUserInfo();
    }
    if (localStorageProvider.get("sso") === "true") {
      return authProvider.currentUserInfo();
    }
    if (isElectronEngine) {
      return electronEngineProvider.currentUserInfo();
    } else {
      return authProvider.currentUserInfo();
    }
  };

  const currentAuthenticatedUser = () =>
    authProvider.currentAuthenticatedUser();

  const login = async ({
    email,
    password,
    token,
    expires_at,
  }: LoginValues & { token?: string; expires_at?: number }) => {
    if (token) {
      return authProvider.ssoLogin({
        token,
        expires_at: expires_at ?? 0,
      });
    }

    if (isElectronEngine) {
      if (password === "") {
        const response = await electronEngineProvider.signInWithPasswordless({
          username: email,
        });

        if (response.status === "error") {
          throw response;
        }

        return response;
      } else {
        const response = await electronEngineProvider.signIn({
          username: email,
          password,
        });

        if (response.status === "error") {
          throw response;
        }

        return response;
      }
    } else {
      return authProvider.signIn(email, password);
    }
  };

  const ssoLogin = async (authParams: {
    token: string;
    expires_at: number;
  }) => {
    return authProvider.ssoLogin(authParams);
  };

  const forgotPassword = async ({ email }: IForgotPasswordValues) => {
    if (isElectronEngine) {
      return electronEngineProvider.forgotPassword({ username: email });
    } else {
      return authProvider.forgotPassword(email);
    }
  };

  const resetPassword = async ({
    email,
    code,
    password,
  }: IResetPasswordValues) => {
    if (isElectronEngine) {
      return electronEngineProvider.confirmPassword({
        username: email,
        code,
        password,
      });
    } else {
      return authProvider.resetPassword(email, code, password);
    }
  };

  const completeNewPasswordChallenge = async ({
    user,
    password,
    email,
  }: Partial<LoginValues> & {
    user?: User;
    password: string;
  }) => {
    if (isElectronEngine) {
      const response = await electronEngineProvider.completeNewPassword({
        password,
      });

      if (response.status === "error") {
        throw response;
      }

      return response;
    } else {
      return authProvider.completeNewPassword(user!, password, {
        email: email!,
      });
    }
  };

  const getAuthenticationToken = async (tokenParam?: string) => {
    if (tokenParam) return tokenParam;
    let session;

    // Handle Z-Pax tokens similar to SSO
    if (localStorageProvider.get("zpax") === "true") {
      const token = localStorageProvider.get("idToken");
      if (token) {
        try {
          const tokenData = parseJwt(token);

          // Check if token is expired
          if (tokenData.exp && tokenData.exp < Math.floor(Date.now() / 1000)) {
            // Clear Z-Pax tokens and logout the user
            localStorageProvider.remove("zpax");
            localStorageProvider.remove("idToken");
            localStorageProvider.remove("accessToken");
            localStorageProvider.remove("refreshToken");
            window.location.replace(window.location.origin + "/#/logout");
            return "";
          }

          return token;
        } catch (error) {
          // If token parsing fails, clear Z-Pax tokens and continue with normal auth
          console.error("Failed to parse Z-Pax token:", error);
          localStorageProvider.remove("zpax");
          localStorageProvider.remove("idToken");
          localStorageProvider.remove("accessToken");
          localStorageProvider.remove("refreshToken");
        }
      } else {
        // If zpax=true but no idToken, clear zpax flag and continue with normal auth
        localStorageProvider.remove("zpax");
      }
    }

    if (localStorageProvider.get("sso") === "true") {
      const { token } = await currentAuthenticatedUser();
      const tokenData = parseJwt(token);

      // Check if token is expired
      if (tokenData.exp && tokenData.exp < Math.floor(Date.now() / 1000)) {
        localStorageProvider.remove("sso");
        localStorageProvider.remove("accessToken");
        localStorageProvider.remove("refreshToken");
        localStorageProvider.remove("idToken");
        localStorageProvider.remove("userInfoUpdated");
        // Logout the user
        window.location.replace(window.location.origin + "/#/logout");
        return "";
      }

      // For testing: Uncomment the line below to simulate token expiration
      // if (tokenData.exp) {
      //
      //   window.location.replace(window.location.origin + "/#/logout");
      //   return "";
      // }

      return token;
    }
    try {
      if (isElectronEngine) {
        const tokens = await electronEngineProvider.retrieveTokens();
        session = tokens.idToken;
      } else {
        session =
          isCircadiaEngine &&
          circadiaEngineProvider.intfv >= 5 &&
          circadiaEngineProvider.isIOS
            ? (await circadiaEngineProvider.cognitoTokens()).IdToken
            : (await authProvider.currentSession()).getIdToken().getJwtToken();
      }
    } catch {
      return "";
    }
    return session;
  };

  const getAuthenticationAccessToken = async () => {
    if (localStorageProvider.get("zpax") === "true") {
      const accessToken = localStorageProvider.get("accessToken");
      // Return access token even if null/undefined - this is normal for Z-Pax
      return accessToken;
    }
    if (localStorageProvider.get("sso") === "true") {
      return localStorageProvider.get("accessToken");
    }
    let session;
    try {
      if (isElectronEngine) {
        const tokens = await electronEngineProvider.retrieveTokens();
        session = tokens.accessToken;
      } else {
        session =
          isCircadiaEngine &&
          circadiaEngineProvider.intfv >= 5 &&
          circadiaEngineProvider.isIOS
            ? (await circadiaEngineProvider.cognitoTokens()).AccessToken
            : (await authProvider.currentSession())
                .getAccessToken()
                .getJwtToken();
      }
    } catch {
      return "";
    }
    return session;
  };

  const completeSMSMFAChallenge = async ({
    user,
    code,
    typeOfChallenge = "SMS_MFA",
  }: SMSMFAValues) => {
    if (isElectronEngine) {
      if (typeOfChallenge === "CUSTOM_CHALLENGE") {
        const response = await electronEngineProvider.confirmPasswordlessMFA({
          code,
        });
        if (response.status === "error") {
          throw response;
        }
        return response;
      } else {
        const response = await electronEngineProvider.confirmMFA({ code });
        if (response.status === "error") {
          throw response;
        }

        return response;
      }
    } else {
      return authProvider.confirmSignIn(user!, code, typeOfChallenge);
    }
  };

  const changePassword = ({
    user,
    oldPassword,
    newPassword,
  }: IChangePasswordUser) => {
    if (isElectronEngine) {
      return electronEngineProvider.changePassword({
        oldPassword,
        newPassword,
      });
    } else {
      return authProvider.changePassword(user!, oldPassword, newPassword);
    }
  };

  const checkPassword = async ({ oldPassword }: ICheckPassword) => {
    return electronEngineProvider.checkPassword({
      oldPassword,
    });
  };

  const logout = () => {
    if (isElectronEngine) {
      return electronEngineProvider.signOut();
    } else {
      return authProvider.signOut();
    }
  };

  const createSSPin = async ({ pin }: ICreateSSPin) => {
    if (isElectronEngine) {
      return electronEngineProvider.setupPin(pin);
    }
    if (
      isCircadiaEngine &&
      circadiaEngineProvider.intfv >= 5 &&
      circadiaEngineProvider.isIOS
    ) {
      const jwtTokenFromEngine = await circadiaEngineProvider.cognitoTokens();
      return circadiaEngineProvider.changeSSPin(
        jwtTokenFromEngine.IdToken,
        pin
      );
    } else {
      const token = await getAuthenticationToken();
      return circadiaEngineProvider.createSSPin(token, pin);
    }
  };

  const saveSSValue = ({ email, key, value }: ISaveSSValue) => {
    return circadiaEngineProvider.saveSSValue(email, key, value);
  };

  const loadSSValue = ({ email, key, value }: ILoadSSValue) => {
    return circadiaEngineProvider.loadSSValue(email, key, value);
  };

  const cognitoProvidePin = (pin: string) => {
    return circadiaEngineProvider.cognitoProvidePin(pin);
  };

  const loadSSValueBio = ({ email, key }: ILoadSSValueBio) => {
    return circadiaEngineProvider.loadSSValueBio(email, key);
  };

  const changeSSPin = async ({ pin }: IChangeSSPin) => {
    const token = await getAuthenticationToken();
    return circadiaEngineProvider.changeSSPin(token, pin);
  };

  return {
    changePassword,
    completeNewPasswordChallenge,
    completeSMSMFAChallenge,
    currentAuthenticatedUser,
    getAuthenticationToken,
    getCurrentSessionPayload,
    getCurrentUserInfo,
    login,
    ssoLogin,
    logout,
    createSSPin,
    saveSSValue,
    loadSSValue,
    loadSSValueBio,
    changeSSPin,
    getAuthenticationAccessToken,
    cognitoProvidePin,
    forgotPassword,
    resetPassword,
    checkPassword,
  };
};
